/**
 * Production-Level Redirect After Login Utility
 * Handles saving and retrieving the previous page before redirecting to login
 * Uses cryptic cookie names and values for security (obfuscation)
 * Only developers should understand this mechanism
 */

const REDIRECT_COOKIE_NAME = "x_rf_p";
const REDIRECT_COOKIE_EXPIRY_MS = 30 * 60 * 1000;
const FALLBACK_REDIRECT_PATH = "/";

const getRedirectCookieMatch = () => {
  if (typeof document === "undefined") return null;
  return document.cookie.match(new RegExp(`(^| )${REDIRECT_COOKIE_NAME}=([^;]+)`));
};

const readRedirectAfterLogin = (): string | null => {
  const match = getRedirectCookieMatch();
  if (!match) return null;

  try {
    return atob(decodeURIComponent(match[2]));
  } catch {
    clearRedirectAfterLogin();
    return null;
  }
};

/**
 * Save the page user is coming from before redirecting to login
 * @param path - The current page path (e.g., "/checkout/cart", "/wishlist", "/product/123")
 */
export const setRedirectAfterLogin = (path: string) => {
  if (typeof document === "undefined") return;

  // Cryptic cookie name: "x_rf_p" (x_redirect_from_page)
  // Cryptic value: Base64 encoded path for obfuscation
  const encodedPath = btoa(path); // Base64 encode path
  const expires = new Date();
  expires.setTime(expires.getTime() + REDIRECT_COOKIE_EXPIRY_MS); // 30 minutes expiry

  const isSecure =
    window.location.protocol === "https:" &&
    !window.location.hostname.includes("localhost");
  const secureStr = isSecure ? ";Secure" : "";

  document.cookie = `${REDIRECT_COOKIE_NAME}=${encodeURIComponent(encodedPath)};expires=${expires.toUTCString()};path=/;SameSite=Strict${secureStr}`;
};

/**
 * Get the saved page path and clear it
 * @returns The previous page path, or "/" as default
 */
export const getRedirectAfterLogin = (): string => {
  const redirectPath = readRedirectAfterLogin();
  clearRedirectAfterLogin();
  return redirectPath || FALLBACK_REDIRECT_PATH;
};

export const hasRedirectAfterLogin = (): boolean => {
  return readRedirectAfterLogin() !== null;
};

/**
 * Manually clear the saved redirect path
 */
export const clearRedirectAfterLogin = () => {
  if (typeof document === "undefined") return;
  // Cryptic cookie name: "x_rf_p"
  document.cookie = `${REDIRECT_COOKIE_NAME}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;SameSite=Strict`;
};
